import React, { useContext, useState } from 'react';
import styled from 'styled-components';

import InputSettingsField from './InputSettingsField';
import FieldStyleSettings from './FieldStyleSettings';
import MetadataSettings from './MetadataSettings';
import CheckboxSettingsField from './CheckboxSettingsField';
import Dropzone from '../../misc/Dropzone';
import AccordionSettings from './AccordionSettings';
import FieldSettingsHeaderTitle from './FieldSettingsHeaderTitle';
import { StyledFieldWrapper } from './SettingsCommonElements';

import FileViews from '../../../constants/FileViews';
import FormStylesOptions from '../../../constants/FormStylesOptions';

import FieldModes from '../../../constants/FieldModes';
import FieldPresentationModes from '../../../constants/FieldPresentationModes';

import { getFieldThemeObject } from '../../../theme/getThemeObject';

import ThemeContext from '../../../theme/ThemeContext';
import { isSettingHidden, isStyleSettingHidden, getMetadataOptions } from '../../../lib/settingsHelper';

import Popconfirm from '../../misc/Popconfirm';

const StyledWrapper = styled.div`
  padding: 12px 0px;
  display: flex;
  flex-direction: column;
`;

const FileSettings = ({

  id,
  name,
  styles,
  metadata,
  config = {},

  //Action handlers
  onChange,
  onUpload,

  //View helpers
  view,
  views,
  onToggleViewSync,

}) => {

  const theme = useContext(ThemeContext);

  const {
    //Title styles
    titleFontSize,
    titleFontColor,
    titleTextAlign,
    titleFontStyle,
    titleFontWeight,
    titleTextTransform,
    titleTextDecoration,

    //Field style
    fontSize,
    fontColor,
    fontStyle,
    fontWeight,
    textAlign,
    textTransform,
    textDecoration,
    textOverflow,
    borderColor,
    borderRadius,
    borderWidth,
    backgroundColor,
    padding,
    margin
  } = getFieldThemeObject({
    mode: FieldModes.fill,
    fieldPresentationMode: FieldPresentationModes.normal,
    fileStyles: styles,
    theme: theme?.field,
    sdkTheme: theme
  });

  const [displayConfirm, setDisplayConfirm] = useState(false);

  const hasMobileView = !views ? false : views.find((view) => view.type === FileViews.mobile);

  //Settings
  const isTitleHidden = isSettingHidden(config, 'title');
  const isUploadHidden = isSettingHidden(config, 'upload');

  //Title Styles
  const isTitleFontSizeHidden = isStyleSettingHidden(config, 'titleFontSize');
  const isTitleFontColorHidden = isStyleSettingHidden(config, 'titleFontColor');
  const isTitleTextAlignHidden = isStyleSettingHidden(config, 'titleTextAlign');
  const isTitleFontStyleHidden = isStyleSettingHidden(config, 'titleFontStyle');
  const isTitleFontWeightHidden = isStyleSettingHidden(config, 'titleFontWeight');
  const isTitleTextTransformHidden = isStyleSettingHidden(config, 'titleTextTransform');
  const isTitleTextDecorationHidden = isStyleSettingHidden(config, 'titleTextDecoration');

  //Text Styles
  const isFontSizeHidden = isStyleSettingHidden(config, 'fontSize');
  const isFontColorHidden = isStyleSettingHidden(config, 'fontColor');
  const isTextAlignHidden = isStyleSettingHidden(config, 'textAlign');
  const isFontStyleHidden = isStyleSettingHidden(config, 'fontStyle');
  const isFontWeightHidden = isStyleSettingHidden(config, 'fontWeight');
  const isTextTransformHidden = isStyleSettingHidden(config, 'textTransform');
  const isTextDecorationHidden = isStyleSettingHidden(config, 'textDecoration');
  const isTextOverflowHidden = isStyleSettingHidden(config, 'textOverflow');

  //Field Styles
  const isBorderColorHidden = isStyleSettingHidden(config, 'borderColor');
  const isBorderRadiusHidden = isStyleSettingHidden(config, 'borderRadius');
  const isBorderWidthHidden = isStyleSettingHidden(config, 'borderWidth');
  const isBackgroundColorHidden = isStyleSettingHidden(config, 'backgroundColor');
  const isMarginHidden = isStyleSettingHidden(config, 'margin');
  const isPaddingHidden = isStyleSettingHidden(config, 'padding');

  const metadataOptions = getMetadataOptions(config);
  const popoverTitle = !hasMobileView ? 'Disable Syncing' : 'Enable Syncing';
  const popoverDescription = !hasMobileView ? 'You will no longer receive changes from the desktop view. Mobile view will be independent. Are you sure you want to continue?'
    : 'You will lose any changes made to the mobile layout view. Are you sure you want to continue?';

  return (
    <StyledWrapper data-testid="form-settings">
      <FieldSettingsHeaderTitle title={view === FileViews.mobile ? 'Mobile Settings' : 'Settings'} />
      {view !== FileViews.mobile ? null : (
        <>
          <StyledFieldWrapper>
            <Popconfirm
              title={popoverTitle}
              description={popoverDescription}
              isOpen={displayConfirm}
              positions={['left']}
              padding={18}
              cancelText="Cancel"
              onCancel={() => setDisplayConfirm(false)}
              confirmText="Confirm"
              containerStyles={{ lineHeight: '16px' }}
              onConfirm={() => {
                setDisplayConfirm(false);
                onToggleViewSync(FileViews.mobile);
              }}
            >
              <div />
            </Popconfirm>
            <CheckboxSettingsField
              id="view-sync"
              label="Field Syncing Enabled"
              description="Custom mobile layouts disabled"
              selected={!hasMobileView}
              onChange={() => setDisplayConfirm(true)}
            />
          </StyledFieldWrapper>
        </>
      )}
      {isTitleHidden ? null : (
        <StyledFieldWrapper>
          <InputSettingsField
            fieldId="name"
            value={name}
            type="text"
            onChange={(fieldId, value) => onChange(id, { name: value })}
          />
        </StyledFieldWrapper>
      )}
      {isUploadHidden || !onUpload || view === FileViews.mobile ? null : (
        <StyledFieldWrapper>
          <Dropzone
            dragMessage="pdf, png, jpg"
            onDrop={(files) => onUpload(id, files[0])}
            accept={{
              'image/jpeg': ['.jpg', '.jpeg', '.JPG', '.JPEG'],
              'image/png': ['.png', '.PNG'],
              'application/pdf': ['.pdf', '.PDF']
            }}
          />
        </StyledFieldWrapper>
      )}
      {!metadataOptions ? null : (
        <>
          <MetadataSettings
            metadata={metadata}
            metadataOptions={metadataOptions}
            onChange={(changes) => {
              const nextMetadata = metadata ? { ...metadata, ...changes } : { ...changes };
              onChange(id, { metadata: nextMetadata });
            }}
          />
        </>
      )}
      {(isTitleFontSizeHidden
        && isTitleFontColorHidden
        && isTitleTextAlignHidden
        && isTitleFontStyleHidden
        && isTitleFontWeightHidden
        && isTitleTextTransformHidden
        && isTitleTextDecorationHidden) ? null : (
          <AccordionSettings
            isOpen
            title="Title"
            height={156}
          >
            <FieldStyleSettings
              id={`${id}-title`}
              {...{
                fontSize: titleFontSize,
                onFontSizeChange: isTitleFontSizeHidden ? null : (value) => onChange(id, { styles: { ...styles, titleFontSize: value } }),

                fontColor: titleFontColor,
                onFontColorChange: isTitleFontColorHidden ? null : (value) => onChange(id, { styles: { ...styles, titleFontColor: value } }),

                textAlign: titleTextAlign,
                onTextAlignChange: isTitleTextAlignHidden ? null : (value) => onChange(id, { styles: { ...styles, titleTextAlign: value } }),

                fontStyle: titleFontStyle,
                onFontStyleChange: isTitleFontStyleHidden ? null : (value) => onChange(id, { styles: { ...styles, titleFontStyle: value } }),

                fontWeight: titleFontWeight || FormStylesOptions.fontWeightBold,
                onFontWeightChange: isTitleFontWeightHidden ? null : (value) => onChange(id, { styles: { ...styles, titleFontWeight: value } }),

                textTransform: titleTextTransform,
                onTextTransformChange: isTitleTextTransformHidden ? null : (value) => onChange(id, { styles: { ...styles, titleTextTransform: value } }),

                textDecoration: titleTextDecoration,
                onTextDecorationChange: isTitleTextDecorationHidden ? null : (value) => onChange(id, { styles: { ...styles, titleTextDecoration: value } }),
              }}
            />
          </AccordionSettings>
        )}
      {(
        isFontSizeHidden
        && isFontColorHidden
        && isTextAlignHidden
        && isFontStyleHidden
        && isFontWeightHidden
        && isTextTransformHidden
        && isTextDecorationHidden
        && isTextOverflowHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Text"
          height={156}
        >
          <FieldStyleSettings
            id={`${id}-field`}
            {...{

              fontSize,
              onFontSizeChange: isFontSizeHidden ? null : (value) => onChange(id, { styles: { ...styles, fontSize: value } }),

              fontColor,
              onFontColorChange: isFontColorHidden ? null : (value) => onChange(id, { styles: { ...styles, fontColor: value } }),

              textAlign,
              onTextAlignChange: isTextAlignHidden ? null : (value) => onChange(id, { styles: { ...styles, textAlign: value } }),

              fontStyle,
              onFontStyleChange: isFontStyleHidden ? null : (value) => onChange(id, { styles: { ...styles, fontStyle: value } }),

              fontWeight,
              onFontWeightChange: isFontWeightHidden ? null : (value) => onChange(id, { styles: { ...styles, fontWeight: value } }),

              textTransform,
              onTextTransformChange: isTextTransformHidden ? null : (value) => onChange(id, { styles: { ...styles, textTransform: value } }),

              textDecoration,
              onTextDecorationChange: isTextDecorationHidden ? null : (value) => onChange(id, { styles: { ...styles, textDecoration: value } }),

              textOverflow,
              onTextOverflowChange: isTextOverflowHidden ? null : (value) => onChange(id, { styles: { ...styles, textOverflow: value } }),

            }}
          />
        </AccordionSettings>
        )}
      {(
        isBorderColorHidden
        && isBorderRadiusHidden
        && isBorderWidthHidden
        && isBackgroundColorHidden
        && isMarginHidden
        && isPaddingHidden
      ) ? null : (
        <AccordionSettings
          isOpen
          title="Field"
          height={200}
        >
          <FieldStyleSettings
            id={`${id}-field`}
            {...{

              borderColor,
              onBorderColorChange: isBorderColorHidden ? null : (value) => onChange(id, { styles: { ...styles, borderColor: value } }),

              borderRadius,
              onBorderRadiusChange: isBorderRadiusHidden ? null : (value) => onChange(id, { styles: { ...styles, borderRadius: value } }),

              borderWidth,
              onBorderWidthChange: isBorderWidthHidden ? null : (value) => onChange(id, { styles: { ...styles, borderWidth: value } }),

              backgroundColor,
              onBackgroundColorChange: isBackgroundColorHidden ? null : (value) => onChange(id, { styles: { ...styles, backgroundColor: value } }),

              margin,
              onMarginChange: isMarginHidden ? null : (value) => onChange(id, { styles: { ...styles, margin: value } }),

              padding,
              onPaddingChange: isPaddingHidden ? null : (value) => onChange(id, { styles: { ...styles, padding: value } }),

            }}
          />
        </AccordionSettings>
        )}
    </StyledWrapper>
  );

};

export default FileSettings;
